import React, { useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { CheckCircle2, ShieldCheck, Printer, ArrowLeft, Building2, Phone, Mail, FileText, Loader2, PackageCheck } from 'lucide-react';
import SuratJalanDocument from '@/Components/production/SuratJalanDocument';

export default function PublicSuratJalanPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const shipmentId = searchParams.get('shipment_id');

  // Fetch orders
  const { data: orders = [], isLoading: loadingOrders } = useQuery({
    queryKey: ['public-surat-jalan-orders'],
    queryFn: () => base44.entities.ProductionOrder.list(),
  });

  // Fetch company settings
  const { data: companySettings = {} } = useQuery({
    queryKey: ['company-settings'],
    queryFn: async () => {
      try {
        const res = await base44.entities.CompanySettings.list();
        return res?.[0] || {};
      } catch (e) {
        return {};
      }
    },
  });

  // Find target order
  const matchedOrder = useMemo(() => {
    if (!id || orders.length === 0) return null;
    const cleanId = decodeURIComponent(id).trim().toLowerCase();

    return orders.find(o => {
      const oid = String(o.id || '').toLowerCase();
      const onum = String(o.order_number || o.po_number || '').toLowerCase();
      const invId = String(o.invoice_id || '').toLowerCase();
      return oid === cleanId || onum === cleanId || invId === cleanId;
    }) || null;
  }, [id, orders]);

  // Target shipment
  const matchedShipment = useMemo(() => {
    if (!matchedOrder) return null;
    if (shipmentId && matchedOrder.shipments?.length > 0) {
      return matchedOrder.shipments.find(s => String(s.id) === String(shipmentId)) || matchedOrder.shipments[0];
    }
    return matchedOrder.shipments?.[0] || null;
  }, [matchedOrder, shipmentId]);

  if (loadingOrders) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-4">
        <Loader2 className="w-10 h-10 text-[#8C6208] animate-spin mb-4" />
        <p className="text-sm font-bold text-slate-700">Memverifikasi Dokumen Surat Jalan Satala...</p>
      </div>
    );
  }

  if (!matchedOrder) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-600 mb-4 shadow-sm">
          <FileText className="w-8 h-8" />
        </div>
        <h1 className="text-xl font-black text-slate-800 mb-2">Surat Jalan Tidak Ditemukan</h1>
        <p className="text-sm text-slate-600 max-w-md mb-6 leading-relaxed">
          Dokumen pengiriman dengan kode <span className="font-bold text-slate-800 font-mono">#{id}</span> belum terbit atau tautan tidak valid. Silakan hubungi tim administrasi Satala Dermatech Essential.
        </p>
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#302621] text-white font-bold text-xs hover:bg-black transition-all shadow-md"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda Satala
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EA] text-slate-800 print:bg-white print:p-0">
      {/* Top Banner Verification Header (Hidden on Print) */}
      <div className="bg-[#302621] text-white border-b border-[#D4AF37]/40 shadow-md sticky top-0 z-50 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs md:text-sm font-black tracking-tight text-white">
                  SURAT JALAN RESMI TERVERIFIKASI
                </span>
                <span className="bg-emerald-500/20 text-emerald-300 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-500/30 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Valid
                </span>
              </div>
              <p className="text-[10px] md:text-xs text-[#D4AF37] font-medium">
                PT. Satala Dermatech Essential · No: {matchedOrder.order_number || matchedOrder.po_number || id}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#D4AF37] text-[#302621] font-bold text-xs hover:bg-[#b89528] transition-all shadow-sm"
            >
              <Printer className="w-3.5 h-3.5" /> Cetak / PDF
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-white/90 hover:bg-white/20 font-semibold text-xs transition-all"
            >
              Beranda
            </Link>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-4xl mx-auto p-4 md:py-8 print:p-0">
        {/* Verification Card */}
        <div className="bg-white border border-emerald-200 rounded-2xl p-4 mb-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 print:hidden">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 shrink-0">
              <PackageCheck className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold text-emerald-800">
                Dokumen Digital Pengiriman Asli
              </p>
              <p className="text-[11px] text-slate-600">
                Surat Jalan ini diterbitkan secara resmi dari sistem logistik pabrik PT. Satala Dermatech Essential untuk brand <span className="font-bold text-slate-800">{matchedOrder.brand_name}</span>.
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Status Pengiriman</span>
            <span className="text-xs font-black text-slate-800 uppercase bg-slate-100 px-2 py-0.5 rounded">
              {matchedOrder.status === 'done' || matchedOrder.is_shipped ? 'Terkirim / Selesai' : 'Sedang Dikirim'}
            </span>
          </div>
        </div>

        {/* The Official Surat Jalan Document Component */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200 print:shadow-none print:border-none p-4 md:p-8 flex justify-center">
          <SuratJalanDocument
            order={matchedOrder}
            shipment={matchedShipment}
            companySettings={companySettings}
          />
        </div>

        {/* Footer info (Hidden on Print) */}
        <div className="mt-8 text-center text-xs text-slate-500 space-y-1 print:hidden">
          <p className="font-semibold text-slate-600">PT. Satala Dermatech Essential</p>
          <p>Jl. Aster Kav, Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat 16610</p>
          <p className="text-[10px] text-slate-400 mt-2">© {new Date().getFullYear()} Satala Dermatech Essential Logistics Portal. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
